<template>
  <div class="aroma-profile">
    <aroma-secondary-nav title="完善基础信息" fallback="AromaReportLogin" />
    <div v-if="profileLoadError" class="aroma-profile__load-error">
      <span>{{ profileLoadError }}</span>
      <van-button size="small" :loading="profileLoading" @click="loadCurrentProfile">重新加载</van-button>
    </div>
    <input-cell title="姓名" :initialValue="realName" @onInput="onRealNameInput"></input-cell>
    <input-cell
      title="性别"
      :initialValue="genderText"
      :isInputable="false"
      @onCellClick="showGenderPicker = true"
    ></input-cell>
    <input-cell
      title="生日"
      :initialValue="birthdayText"
      :isInputable="false"
      @onCellClick="showBirthdayPicker = true"
    ></input-cell>
    <input-cell title="身高cm" :initialValue="height" inputType="number" @onInput="onHeightInput"></input-cell>
    <input-cell title="体重kg" :initialValue="weight" inputType="number" @onInput="onWeightInput"></input-cell>
    <round-button class="aroma-profile__button" color="red" title="保存并查看报告" @onClick="onSubmit"></round-button>

    <van-popup v-model="showGenderPicker" round position="bottom">
      <van-picker
        title="选择性别"
        show-toolbar
        :columns="genderOptions"
        :default-index="genderSelectedIndex"
        @confirm="onGenderPicked"
        @cancel="showGenderPicker = false"
      />
    </van-popup>

    <van-popup v-model="showBirthdayPicker" round position="bottom">
      <van-datetime-picker
        title="选择生日"
        show-toolbar
        v-model="currentDate"
        type="date"
        :min-date="minDate"
        :max-date="maxDate"
        @confirm="onBirthdayPicked"
        @cancel="showBirthdayPicker = false"
      />
    </van-popup>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import API from '@/api'
import InputCell from '@/components/common/input-cell.vue'
import RoundButton from '@/components/common/round-button.vue'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import { applySniCurrentResult, clearAllSniSessions, extractSniProfile, getActiveSniSession, normalizeSniSn, updateSniProfile } from '@/utils/sni-session'
import { formatSniProfileBirthday } from '@/utils/sni-profile'
import { getAromaEntryLocation } from '@/utils/aroma-route'
import { releaseActiveInput, resetViewportScroll } from '@/utils/viewport'

export default {
  name: 'AromaReportProfile',

  components: {
    InputCell,
    RoundButton,
    AromaSecondaryNav
  },

  data() {
    return {
      realName: '',
      sex: '',
      birthday: undefined,
      currentDate: new Date(2000, 0, 1),
      height: '',
      weight: '',
      showGenderPicker: false,
      showBirthdayPicker: false,
      genderOptions: ['男', '女'],
      minDate: new Date(1900, 0, 1),
      maxDate: new Date(),
      profileLoading: false,
      profileLoadError: ''
    }
  },

  computed: {
    sn() {
      return normalizeSniSn(this.$route.query.sn)
    },

    session() {
      return getActiveSniSession(this.sn)
    },

    token() {
      return this.session?.token
    },

    redirect() {
      return this.$route.query.redirect || ''
    },

    genderText() {
      if (this.sex === '0') return '男'
      if (this.sex === '1') return '女'
      return ''
    },

    genderSelectedIndex() {
      return this.sex === '1' ? 1 : 0
    },

    birthdayText() {
      return this.birthday ? dayjs(this.birthday).format('YYYY-MM-DD') : ''
    }
  },

  mounted() {
    this.loadCurrentProfile()
  },

  methods: {
    async loadCurrentProfile() {
      if (!this.token) {
        this.$router.replace({ name: 'AromaReportLogin', query: this.sn ? { sn: this.sn } : {} })
        return
      }
      this.profileLoading = true
      this.profileLoadError = ''
      try {
        const current = await API.sniCurrent(this.token)
        applySniCurrentResult(current, this.token)
        const profile = extractSniProfile(current)
        this.realName = profile.realName || ''
        this.sex = profile.sex === '0' || profile.sex === '1' ? profile.sex : ''
        this.birthday = profile.birthday ? dayjs(profile.birthday).toDate() : undefined
        this.currentDate = this.birthday || this.currentDate
        this.height = profile.height ? String(profile.height) : ''
        this.weight = profile.weight ? String(profile.weight) : ''
        updateSniProfile(profile, this.sn)
      } catch (error) {
        if (error?.code === '8' || error?.code === '11') {
          clearAllSniSessions()
          this.$router.replace({ name: 'AromaReportLogin', query: this.sn ? { sn: this.sn } : {} })
          return
        }
        this.profileLoadError = error?.message || '个人资料加载失败，请重试'
      } finally {
        this.profileLoading = false
      }
    },

    onRealNameInput(value) {
      this.realName = value
    },

    onHeightInput(value) {
      this.height = value
    },

    onWeightInput(value) {
      this.weight = value
    },

    onGenderPicked(value, index) {
      this.showGenderPicker = false
      this.sex = index === 0 ? '0' : '1'
    },

    onBirthdayPicked(value) {
      this.showBirthdayPicker = false
      this.birthday = value
      this.currentDate = value
    },

    validateForm() {
      if (!this.realName.trim()) return '姓名不能为空'
      if (!this.sex) return '性别不能为空'
      if (!this.birthday) return '生日不能为空'
      if (!this.height) return '身高不能为空'
      if (!this.weight) return '体重不能为空'
      const height = Number(this.height)
      const weight = Number(this.weight)
      if (Number.isNaN(height) || height < 30 || height > 250) return '身高需在30-250cm之间'
      if (Number.isNaN(weight) || weight < 2 || weight > 500) return '体重需在2-500kg之间'
      return ''
    },

    async onSubmit() {
      const message = this.validateForm()
      if (message) {
        this.$toast(message)
        return
      }
      if (!this.token) {
        this.$router.replace({ name: 'AromaReportLogin', query: this.sn ? { sn: this.sn } : {} })
        return
      }
      try {
        const profile = await API.sniCompleteProfile({
          realName: this.realName.trim(),
          sex: this.sex,
          birthday: formatSniProfileBirthday(dayjs(this.birthday).format('YYYY-MM-DD')),
          height: Number(this.height),
          weight: Number(this.weight)
        }, this.token)
        updateSniProfile(profile, this.sn)
        releaseActiveInput()
        await this.$router.replace(getAromaEntryLocation(this.sn, this.redirect))
        resetViewportScroll()
      } catch (error) {
        this.$toast(error?.message || error)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.aroma-profile {
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 8px;
  overflow-x: hidden;

  &__button {
    width: 343px;
    margin-top: 35px;
  }

  &__load-error {
    display: flex;
    width: 343px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 8px 0;
    padding: 10px 12px;
    border: 1px solid #ffd8d6;
    border-radius: 6px;
    color: #d6463f;
    background: #fff5f4;
    box-sizing: border-box;
    font-size: 13px;
  }

}
</style>
